import React, { createContext, useContext, useEffect, useState } from 'react'
import { authService } from '../services/authService'
import { UserDTO, Role } from '../types/backend'
import { setAuthToken } from '../services/api' // Import the new function

interface AuthContextType {
  user: UserDTO | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export { AuthContext }

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserDTO | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const storedToken = authService.getToken()
    const storedUser = authService.getStoredUser()

    if (storedToken && storedUser) {
      setToken(storedToken)
      setUser(storedUser)
      setAuthToken(storedToken) // Apply the token to the API client on initial load
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    try {
      // Authenticate and then fetch complete user profile
      const loginResponse = await authService.login({ email, password })
      setToken(loginResponse.token)

      // Prefer authoritative user data from backend
      const currentUser = await authService.getCurrentUser()
      setUser(currentUser)
      
    } catch (error: any) {
      console.error('💥 AuthContext: Login failed:', {
        error: error.message,
        stack: error.stack
      });
      throw error
    }
  }

  const logout = () => {
    authService.logout()
    setUser(null)
    setToken(null)
  }

  // Listen for centralized auth logout events (401s)
  useEffect(() => {
    const handler = () => logout()
    window.addEventListener('app:auth-logout', handler)
    return () => window.removeEventListener('app:auth-logout', handler)
  }, [])

  const value = {
    user,
    token,
    login,
    logout,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
